(() => {
  'use strict';
  const config = window.O2O || {};
  const rootPath = config.routes?.root || window.Shopify?.routes?.root || '/';
  const routes = {cart: `${rootPath}cart`, cartAdd: `${rootPath}cart/add`, cartChange: `${rootPath}cart/change`, cartUpdate: `${rootPath}cart/update`, ...config.routes};
  const strings = JSON.parse(document.getElementById('O2OCommerceStrings')?.textContent || '{}');
  const text = key => strings[key] || key;
  const parseHTML = html => new DOMParser().parseFromString(html, 'text/html');
  const escapeHTML = value => String(value ?? '').replace(/[&<>"']/g, char => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[char]));
  const jsonRoute = route => route.endsWith('.js') ? route : `${route}.js`;
  const status = message => { const mount = document.querySelector('[data-commerce-status]'); if (mount) mount.textContent = message; };
  const storage = {
    read(key) {try {const data = JSON.parse(localStorage.getItem(key) || '[]'); return Array.isArray(data) ? data.filter(item => typeof item === 'string').slice(0, 100) : [];} catch {return []; }},
    write(key, data) {try {localStorage.setItem(key, JSON.stringify(data));} catch {} }
  };
  const recentKey = 'o2o-shopify-recent';
  let cartQueue = Promise.resolve();
  let noteTimer;
  let quickController;
  let lastCartRefresh = 0;
  const productRequests = new Map();
  const variantControllers = new WeakMap();
  const zoomLevels = [1, 1.5, 2, 2.5, 3];
  const sectionIds = () => [...document.querySelectorAll('[data-cart-section]')].map(node => node.dataset.cartSection);
  const surfaceOpen = id => window.O2OShell?.open(id);
  const surfaceClose = id => window.O2OShell?.close(id);
  const money = cents => {
    const currency = config.currency || window.Shopify?.currency?.active || 'INR';
    const amount = Number(cents) / 100;
    const wholeINR = currency === 'INR' && Number.isInteger(amount);
    try {return new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency', currency, ...(wholeINR ? {minimumFractionDigits:0, maximumFractionDigits:0} : {})}).formatToParts(amount).map(part => part.type === 'currency' ? `${part.value} ` : part.value).join('').replace(/\s+/g,' ').trim();} catch {return String(amount);}
  };
  async function request(url, options = {}) {
    const response = await fetch(url, {credentials: 'same-origin', ...options});
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || text('error'));
    return data;
  }
  function cartError(error) {
    document.querySelectorAll('[data-cart-error]').forEach(node => {node.textContent = error.message || text('error'); node.hidden = false;});
    status(error.message || text('error'));
  }
  function updateCartBadges(cart) {
    document.querySelectorAll('[data-cart-count]').forEach(node => {node.textContent = cart.item_count; node.hidden = !cart.item_count;});
    document.dispatchEvent(new CustomEvent('o2o:cart-updated', {detail: cart}));
  }
  function renderCartSections(sections) {
    document.querySelectorAll('[data-cart-section]').forEach(section => {
      const html = sections?.[section.dataset.cartSection];
      if (!html) return;
      const replacement = parseHTML(html).querySelector('[data-cart-content]');
      if (replacement) section.querySelector('[data-cart-content]')?.replaceWith(replacement);
    });
    document.dispatchEvent(new CustomEvent('o2o:content-updated'));
  }
  async function refreshCart(data) {
    let cart = data?.item_count != null ? data : null;
    if (!cart && data?.sections) {
      for (const html of Object.values(data.sections)) {
        if (!html) continue;
        try {cart=JSON.parse(parseHTML(html).querySelector('[data-cart-state]')?.dataset.cartState || 'null');} catch {}
        if (cart?.item_count != null) break;
      }
    }
    if (cart?.item_count == null) cart = await request(jsonRoute(routes.cart));
    updateCartBadges(cart);
    if (data?.sections && Object.values(data.sections).every(Boolean)) renderCartSections(data.sections);
    else {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', sectionIds().join(','));
      renderCartSections(await request(url.toString()));
    }
    lastCartRefresh = Date.now();
    return cart;
  }
  function enqueueCart(callback) {
    cartQueue = cartQueue.catch(() => {}).then(callback);
    return cartQueue;
  }
  async function mutateCart(route, payload) {
    document.querySelectorAll('[data-cart-content]').forEach(node => node.setAttribute('aria-busy', 'true'));
    try {
      const data = await request(jsonRoute(route), {method: 'POST', headers: {'Content-Type':'application/json', Accept:'application/json'}, body: JSON.stringify({...payload, sections: sectionIds(), sections_url: window.location.pathname})});
      await refreshCart(data);
      return data;
    } finally {document.querySelectorAll('[data-cart-content]').forEach(node => node.removeAttribute('aria-busy'));}
  }
  function changeQuantity(line, quantity) {
    if (!line) return;
    const key = line.dataset.cartKey;
    const restoreQuantity = () => document.querySelectorAll(`[data-cart-key="${CSS.escape(key)}"] [data-cart-quantity]`).forEach(input => {input.value=input.defaultValue;});
    if (!Number.isInteger(quantity) || quantity < 0) {restoreQuantity();return;}
    enqueueCart(() => mutateCart(routes.cartChange, {id:key, quantity})).catch(error => {restoreQuantity();cartError(error);});
  }
  async function addProduct(form) {
    if (form.dataset.adding === 'true') return;
    if (form.dataset.variantPending === 'true' || form.querySelector('[name="id"]')?.disabled) return;
    if (!form.reportValidity()) return;
    const errorNode = form.querySelector('[data-form-error]');
    if (errorNode) errorNode.hidden = true;
    const length = form.querySelector('[data-length-adjustment]');
    if (length && !/^(Standard|[+-][1-5] inches)$/.test(length.value)) throw new Error(text('invalidLength'));
    const productRoot = form.closest('[data-product-root]');
    const formId = form.getAttribute('id');
    const buttons = [...form.querySelectorAll('[type="submit"]'), ...document.querySelectorAll(`[form="${CSS.escape(formId || '')}"][type="submit"]`)];
    buttons.forEach(button => {button.disabled = true; button.setAttribute('aria-busy','true');});
    const data = new FormData(form);
    form.dataset.adding = 'true';
    data.set('sections', sectionIds().join(','));
    data.set('sections_url', window.location.pathname);
    try {
      await enqueueCart(async () => {
        const added = await request(jsonRoute(routes.cartAdd), {method:'POST', headers:{Accept:'application/json'}, body:data});
        await refreshCart(added);
      });
      surfaceClose('quickViewModal');
      const customDrawer = document.getElementById(productRoot?.dataset.customDrawerId || '');
      if (customDrawer) surfaceClose(customDrawer.id);
      surfaceOpen('cartDrawer');
      status(text('added'));
    } catch (error) {
      if (errorNode) {errorNode.hidden = false; errorNode.textContent = error.message;}
      const customError = document.getElementById(productRoot?.dataset.customDrawerId || '')?.querySelector('[data-custom-error]');
      if (customError) {customError.hidden = false; customError.textContent = error.message;}
      status(error.message);
    } finally {delete form.dataset.adding;buttons.forEach(button => {button.disabled = false; button.removeAttribute('aria-busy');});}
  }
  function namespaceQuick(root) {
    root.querySelectorAll('[id]').forEach(node => {node.setAttribute('id', `Quick-${node.getAttribute('id').replace(/^Quick-/, '')}`);});
    root.dataset.customDrawerId = `Quick-${root.dataset.customDrawerId.replace(/^Quick-/, '')}`;
    ['for','form','aria-controls','aria-labelledby','aria-describedby','data-commerce-open','data-commerce-close'].forEach(attribute => root.querySelectorAll(`[${attribute}]`).forEach(node => {
      node.setAttribute(attribute, node.getAttribute(attribute).split(' ').map(id => `Quick-${id.replace(/^Quick-/, '')}`).join(' '));
    }));
  }
  function variantData(root) {
    try {return JSON.parse(root.querySelector('[data-variant-data]')?.textContent || '{}');} catch {return {};}
  }
  function customDrawerFor(root) {
    return document.getElementById(root.dataset.customDrawerId) || root.querySelector('[data-custom-drawer]');
  }
  function syncMobileProductActions() {
    const mobile = window.matchMedia('(max-width:749px)').matches;
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const viewportTop = window.visualViewport?.offsetTop || 0;
    document.querySelectorAll('[data-product-root]').forEach(root => {
      const action = root.querySelector('.o2o-mobile-product-action');
      if (!action) return;
      const button = root.querySelector('[data-product-submit]');
      const rect = button?.getBoundingClientRect();
      const hasButton = Boolean(rect && rect.width > 0 && rect.height > 0);
      const regularVisible = hasButton && rect.bottom > viewportTop && rect.top < viewportTop + viewportHeight;
      action.hidden = !mobile || root.dataset.quick === 'true' || !hasButton || regularVisible;
    });
  }
  let mobileActionFrame;
  function scheduleMobileProductActions() {
    if (mobileActionFrame) return;
    mobileActionFrame = requestAnimationFrame(() => {
      mobileActionFrame = null;
      syncMobileProductActions();
    });
  }
  function updateVariantURL(root, variant) {
    if (root.dataset.quick === 'true') return;
    const url = new URL(root.dataset.productUrl, location.origin);
    if (variant) url.searchParams.set('variant', variant.id);
    else url.searchParams.set('option_values', [...root.querySelectorAll('[data-option-value-id]:checked')].map(node=>node.dataset.optionValueId).join(','));
    const current = new URL(location.href);
    ['preview_theme_id','pb'].forEach(key=>{if(current.searchParams.has(key))url.searchParams.set(key,current.searchParams.get(key));});
    history.replaceState(history.state,'',url);
  }
  function applyVariant(root, variant, data, openCustom = false) {
    const form = root.querySelector('[data-product-form]');
    const id = form.querySelector('[name="id"]');
    const selected = [...root.querySelectorAll('[data-option-value-id]:checked')].map(node=>node.value);
    const custom = Boolean(variant?.options?.some(value=>/custom|made to measure/i.test(value)));
    const available = Boolean(variant?.available);
    const labels = data.labels;
    id.value = variant?.id || '';
    id.disabled = !variant;
    delete form.dataset.variantPending;
    root.dataset.selectedOptions = JSON.stringify(selected);
    root.querySelector('[data-product-info]')?.removeAttribute('aria-busy');
    root.querySelectorAll('[data-option-value-id]').forEach(input=>{
      const label = input.nextElementSibling;
      label?.classList.toggle('is-active',input.checked);
      if (data.complete) {
        const index = Number(input.closest('[data-option-position]').dataset.optionPosition)-1;
        const availableOption = data.variants.some(item=>item.available && item.options[index]===input.value && selected.slice(0,index).every((value,i)=>item.options[i]===value));
        label?.classList.toggle('is-unavailable', !availableOption);
      }
    });
    const price = root.querySelector('[data-product-price]');
    if (price) {
  price.innerHTML = variant
    ? `<span class="money">${escapeHTML(money(variant.price))}</span>${
        variant.compare_at_price != null &&
        Number(variant.compare_at_price) !== Number(variant.price)
          ? ` <s>${escapeHTML(money(variant.compare_at_price))}</s>`
          : ''
      }`
    : '';
}
    const mobilePrice = root.querySelector('.o2o-mobile-product-action strong');
    if (mobilePrice) mobilePrice.textContent = variant ? money(variant.price) : '';
    root.querySelectorAll('[data-product-submit],.o2o-mobile-product-action button').forEach(button=>{
      const mobile = Boolean(button.closest('.o2o-mobile-product-action'));
      button.disabled = !available || form.dataset.adding === 'true';
      button.type = custom ? 'button' : 'submit';
      if (mobile && !custom) button.setAttribute('form',form.getAttribute('id'));
      if (custom) {button.dataset.customOpen='';button.dataset.commerceOpen=root.dataset.customDrawerId;button.removeAttribute('form');}
      else {delete button.dataset.customOpen;delete button.dataset.commerceOpen;}
      button.textContent = !variant ? labels.unavailable : !available ? labels.soldOut : `${custom ? labels.custom : mobile ? labels.bag : labels.add}${mobile ? '' : ` · ${money(variant.price)}`}`;
    });
    const drawer = customDrawerFor(root);
    if (drawer) {
      drawer.querySelector('[data-custom-fields]').disabled = !custom;
      const submit = drawer.querySelector('[data-custom-submit]');
      submit.disabled = !available;
      submit.textContent = `${labels.customAdd}${variant ? ` · ${money(variant.price)}` : ''}`;
      drawer.querySelector('[data-custom-error]').hidden = true;
    }
    form.querySelectorAll('.shopify-payment-button').forEach(node=>{node.hidden=custom || !available;});
    const error = form.querySelector('[data-form-error]');
    if (error) {error.hidden=Boolean(variant);error.textContent=variant ? '' : labels.unavailable;}
    const imageName=url=>{try{return new URL(url,location.origin).pathname.split('/').pop();}catch{return '';}};
    const featuredImageName=variant?.featured_image?.src ? imageName(variant.featured_image.src) : '';
    const media=variant?.featured_media?.id ? root.querySelector(`[data-media-id="${variant.featured_media.id}"]`) : featuredImageName ? [...root.querySelectorAll('[data-media-id]')].find(node=>imageName(node.querySelector('img')?.getAttribute('src'))===featuredImageName) : null;
    if (media) {
      root.dataset.variantMedia=media.dataset.mediaId;
      const gallery=root.querySelector('[data-product-gallery]');
      if(media && gallery && (matchMedia('(max-width:749px)').matches || root.dataset.quick==='true'))scrollGalleryToMedia(gallery,media);
    }
    updateVariantURL(root,variant);
    syncMobileProductActions();
    if(custom && available && openCustom && drawer)surfaceOpen(root.dataset.customDrawerId);
  }
  async function selectVariant(input) {
    const root = input.closest('[data-product-root]');
    if (!root) return;
    const previousController=variantControllers.get(root);
    previousController?.abort();
    previousController?.restoreButtons?.();
    const data = variantData(root);
    const selected = [...root.querySelectorAll('[data-option-value-id]:checked')].map(node=>node.value);
    const productURL = new URL(input.dataset.productUrl || root.dataset.productUrl, location.origin);
    if (data.complete && productURL.pathname === new URL(root.dataset.productUrl, location.origin).pathname) {
      const variant = data.variants.find(item=>item.options.every((value,index)=>value===selected[index]));
      applyVariant(root,variant,data,true);
      return;
    }
    const controller = new AbortController();
    variantControllers.set(root, controller);
    const url = new URL(input.dataset.productUrl || root.dataset.productUrl, window.location.origin);
    const values = [...root.querySelectorAll('[data-option-value-id]:checked')].map(node => node.dataset.optionValueId);
    url.searchParams.set('option_values', values.join(','));
    url.searchParams.set('section_id', root.dataset.sectionId);
    const properties = [...root.querySelectorAll('[name^="properties["]')].map(node => [node.name,node.value]);
    const sellingPlan = root.querySelector('[name="selling_plan"]')?.value;
    const info = root.querySelector('[data-product-info]');
    const selectedValue = input.dataset.optionValueId;
    const form = info.querySelector('[data-product-form]');
    const previousOptions = JSON.parse(root.dataset.selectedOptions || JSON.stringify(data.selected?.options || []));
    const previousButtons = [...root.querySelectorAll('[data-product-submit],.o2o-mobile-product-action button')].map(node=>[node,node.disabled]);
    controller.restoreButtons=()=>{previousButtons.forEach(([node,disabled])=>{if(node.isConnected)node.disabled=disabled;});delete form.dataset.variantPending;};
    form.dataset.variantPending='true';
    info?.setAttribute('aria-busy','true');
    root.querySelectorAll('[data-product-submit],.o2o-mobile-product-action button').forEach(node => {node.disabled = true;});
    try {
      const response = await fetch(url, {credentials:'same-origin', signal:controller.signal});
      if (!response.ok) throw new Error(text('error'));
      const next = parseHTML(await response.text()).querySelector('[data-product-root]');
      if (!next) throw new Error(text('error'));
      if (controller.signal.aborted) return;
      if (root.dataset.quick === 'true') namespaceQuick(next);
      const nextInfo = next.querySelector('[data-product-info]');
      properties.forEach(([name,value]) => [...nextInfo.querySelectorAll('[name]')].filter(node => node.name === name).forEach(node => {node.value = value;}));
      if (sellingPlan && nextInfo.querySelector(`[name="selling_plan"] option[value="${CSS.escape(sellingPlan)}"]`)) nextInfo.querySelector('[name="selling_plan"]').value = sellingPlan;
      info.replaceWith(nextInfo);
      const oldDrawer = customDrawerFor(root);
      const customValues = [...(oldDrawer?.querySelectorAll('[name]') || [])].map(node=>[node.name,node.value]);
      oldDrawer?.remove();
      const customDrawer = next.querySelector('[data-custom-drawer]');
      if (customDrawer) {customValues.forEach(([name,value])=>[...customDrawer.querySelectorAll('[name]')].filter(node=>node.name===name).forEach(node=>{node.value=value;}));root.append(customDrawer);}
      root.querySelector('[data-variant-data]')?.replaceWith(next.querySelector('[data-variant-data]'));
      const nextAction = next.querySelector('.o2o-mobile-product-action');
      if (nextAction && root.querySelector('.o2o-mobile-product-action')) root.querySelector('.o2o-mobile-product-action').replaceWith(nextAction);
      if (root.dataset.productUrl !== next.dataset.productUrl) {
        root.querySelector('.pdp-gallery-wrap')?.replaceWith(next.querySelector('.pdp-gallery-wrap'));
        root.dataset.productUrl = next.dataset.productUrl;
        root.dataset.productHandle = next.dataset.productHandle;
        initGalleries(root);
      }
      root.dataset.variantMedia = next.dataset.variantMedia;
      const variantMedia = root.querySelector(`[data-media-id="${CSS.escape(next.dataset.variantMedia || '')}"]`);
      const gallery = root.querySelector('[data-product-gallery]');
      if (variantMedia && gallery && (window.matchMedia('(max-width:749px)').matches || root.dataset.quick === 'true')) scrollGalleryToMedia(gallery,variantMedia);
      root.querySelector(`[data-option-value-id="${CSS.escape(selectedValue)}"]`)?.focus({preventScroll:true});
      const nextData = variantData(root);
      applyVariant(root,nextData.selected,nextData,true);
      if(root.dataset.quick==='true')trimQuickInfo(root);
    } catch (error) {
      if (error.name !== 'AbortError') {
        root.querySelectorAll('[data-option-value-id]').forEach(node=>{const index=Number(node.closest('[data-option-position]').dataset.optionPosition)-1;node.checked=node.value===previousOptions[index];});
        controller.restoreButtons();
        const node = info.querySelector('[data-form-error]');
        if (node) {node.textContent = error.message; node.hidden = false;}
        status(error.message);
      }
    } finally {if (!controller.signal.aborted) {root.querySelector('[data-product-info]')?.removeAttribute('aria-busy');delete controller.restoreButtons;}}
  }
  async function productPage(url, signal) {
    const response = await fetch(url, {credentials:'same-origin',signal});
    if (!response.ok) throw new Error(text('error'));
    const dom = parseHTML(await response.text());
    const product = dom.querySelector('[data-product-root]');
    if (!product) throw new Error(text('error'));
    return product;
  }
  function trimQuickInfo(product) {
    product.querySelectorAll('.pdp-breadcrumb-row,.breadcrumbs,.pdp-usps,.accordion-list,.share-button').forEach(node=>node.remove());
    product.querySelectorAll('[data-product-zoom]').forEach(button=>{button.querySelector('.pdp-zoom-badge')?.remove();button.replaceWith(...button.childNodes);});
    const title=product.querySelector('h1');
    if(title){const h2=document.createElement('h2');h2.className=title.className;h2.textContent=title.textContent;title.replaceWith(h2);}
    if(!product.querySelector('[data-quick-full-details]')) {
      const link=document.createElement('a');link.className='text-link';link.dataset.quickFullDetails='';link.href=product.dataset.productUrl;link.textContent=text('details');product.querySelector('[data-product-info]').append(link);
    }
  }
  function initSizeGuides(scope=document) {
    scope.querySelectorAll('[data-size-guide-content]').forEach(content=>{
      if(content.dataset.ready)return;
      content.dataset.ready='true';
      const modal=content.closest('.o2o-size-guide');
      const tabs=modal.querySelector('[data-size-tabs]');
      const tables=[...content.querySelectorAll('table')];
      tables.forEach((table,index)=>{
        table.classList.add('size-table');
        const wrap=document.createElement('div');wrap.className='size-table-wrap';wrap.dataset.sizePanel=String(index);
        table.replaceWith(wrap);wrap.append(table);
        if(tabs && tables.length===2){wrap.hidden=index!==0;wrap.setAttribute('role','tabpanel');}
      });
      if(tabs && tables.length===2)tabs.hidden=false;
    });
  }
  async function quickView(url, quickAdd = false) {
    quickController?.abort();
    quickController = new AbortController();
    const content = document.querySelector('[data-quick-view-content]');
    if (!content) {window.location.href = url; return;}
    content.innerHTML = `<p class="o2o-quick-loading" role="status">${escapeHTML(text('loading'))}</p>`;
    surfaceOpen('quickViewModal');
    try {
      const product = await productPage(url, quickController.signal);
      product.dataset.quick = 'true';
      [...product.children].filter(node => !node.matches('.pdp-layout,[data-variant-data],[data-custom-drawer],.o2o-size-guide')).forEach(node => node.remove());
      trimQuickInfo(product);
      namespaceQuick(product);
      content.replaceChildren(product);
      initGalleries(product);
      initSizeGuides(product);
      if (quickAdd && !product.querySelector('[data-option-value-id]') && !product.querySelector('[data-length-adjustment]')) {
        const form = product.querySelector('[data-product-form]');
        if (form?.querySelector('[data-product-submit]:not([disabled])')) await addProduct(form);
      } else product.querySelector('[data-option-value-id],select,[data-product-submit]')?.focus({preventScroll:true});
    } catch (error) {
      if (error.name !== 'AbortError') {content.innerHTML = `<p class="o2o-quick-loading" role="alert">${escapeHTML(error.message)}</p><a class="button" href="${escapeHTML(url)}">${escapeHTML(text('details'))}</a>`;}
    }
  }
  async function fetchProduct(handle) {
    if (!/^[a-z0-9][a-z0-9-]*$/i.test(handle)) throw new Error(text('error'));
    if (!productRequests.has(handle)) productRequests.set(handle,request(`${rootPath}products/${encodeURIComponent(handle)}.js`).catch(error => {productRequests.delete(handle);throw error;}));
    return productRequests.get(handle);
  }
  function storedProductCard(product) {
    const url = `${rootPath}products/${encodeURIComponent(product.handle)}`;
    const image = product.featured_image || product.images?.[0];
    const photo = image ? `<img class="product-primary" src="${escapeHTML(image)}" alt="${escapeHTML(product.title)}" width="600" height="750" loading="lazy">` : '';
    const price = cardPrice(product);
    const amount = `${price.varies ? `${escapeHTML(text('from'))} ` : ''}${escapeHTML(money(price.price))}`;
    const comparison = price.compareAt && price.compareAt !== price.price ? ` <s>${escapeHTML(money(price.compareAt))}</s>` : '';
    return `<article class="product-card" data-product-handle="${escapeHTML(product.handle)}"><div class="product-media-wrap"><a class="product-media" href="${url}">${photo}</a><div class="product-card-actions"><button class="product-bag-button" type="button" data-quick-view="${url}" aria-label="${escapeHTML(text('quickView'))} ${escapeHTML(product.title)}"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><path d="M6 7h12l2 14H4L6 7Z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/></svg></button></div></div><div class="product-card-info"><h3><a href="${url}">${escapeHTML(product.title)}</a></h3><p><span class="money">${amount}${comparison}</span></p></div></article>`;
  }
  function cardPrice(product) {
    const fallback = {price:Number(product.price), compareAt:Number(product.compare_at_price || 0), varies:Boolean(product.price_varies)};
    if (!fallback.varies) return fallback;
    const variants = product.variants || [];
    const complete = product.variants_count != null ? variants.length === Number(product.variants_count) : variants.length < 250;
    if (!variants.length || !complete) return fallback;
    const standard = variants.filter(variant => !variant.options?.some(option => /custom|made[\s_-]+to[\s_-]+measure/i.test(option)));
    if (!standard.length) return fallback;
    const lowest = standard.reduce((minimum, variant) => Number(variant.price) < Number(minimum.price) ? variant : minimum);
    return {price:Number(lowest.price), compareAt:Number(lowest.compare_at_price || 0), varies:standard.some(variant => Number(variant.price) !== Number(lowest.price))};
  }
  function galleryMediaOffset(gallery, media) {
    return media.getBoundingClientRect().left - gallery.getBoundingClientRect().left + gallery.scrollLeft;
  }
  function scrollGalleryToMedia(gallery, media) {
    if (!gallery || !media) return;
    gallery.scrollTo({left:galleryMediaOffset(gallery,media),behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'});
  }
  function setProductZoom(dialog, requestedLevel) {
    const image=dialog?.querySelector('[data-product-zoom-image]');
    const stage=dialog?.querySelector('[data-product-zoom-stage]');
    if(!image?.naturalWidth || !stage)return;
    const level=zoomLevels.reduce((nearest,value)=>Math.abs(value-requestedLevel)<Math.abs(nearest-requestedLevel)?value:nearest,1);
    const inset=window.matchMedia('(max-width:749px)').matches?20:32;
    const availableWidth=Math.max(1,stage.clientWidth-inset);
    const availableHeight=Math.max(1,stage.clientHeight-inset);
    const ratio=image.naturalWidth/image.naturalHeight;
    let baseWidth=Math.min(image.naturalWidth,availableWidth);
    let baseHeight=baseWidth/ratio;
    if(baseHeight>availableHeight){baseHeight=Math.min(image.naturalHeight,availableHeight);baseWidth=baseHeight*ratio;}
    image.style.width=`${baseWidth*level}px`;
    image.style.height=`${baseHeight*level}px`;
    dialog.dataset.zoomLevel=String(level);
    dialog.classList.toggle('is-zoomed',level>1);
    dialog.querySelector('[data-product-zoom-level]').textContent=`${level}×`;
    dialog.querySelector('[data-product-zoom-out]').disabled=level===zoomLevels[0];
    dialog.querySelector('[data-product-zoom-in]').disabled=level===zoomLevels[zoomLevels.length-1];
    requestAnimationFrame(()=>stage.scrollTo({left:Math.max(0,(stage.scrollWidth-stage.clientWidth)/2),top:Math.max(0,(stage.scrollHeight-stage.clientHeight)/2),behavior:'auto'}));
  }
  function changeProductZoom(dialog, direction) {
    const current=Number(dialog?.dataset.zoomLevel||1);
    const index=Math.max(0,zoomLevels.indexOf(current));
    setProductZoom(dialog,zoomLevels[Math.max(0,Math.min(zoomLevels.length-1,index+direction))]);
  }
  function openProductZoom(trigger) {
    const root=trigger.closest('[data-product-root]');
    const dialog=root?.querySelector('[data-product-zoom-dialog]');
    const image=dialog?.querySelector('[data-product-zoom-image]');
    if(!dialog || !image)return;
    dialog.zoomOpener=trigger;
    image.alt=trigger.dataset.zoomAlt||'';
    image.src=trigger.dataset.zoomSrc;
    dialog.dataset.zoomLevel='1';
    dialog.showModal();
    image.decode().catch(()=>{}).finally(()=>setProductZoom(dialog,1));
    dialog.querySelector('[data-product-zoom-close]')?.focus({preventScroll:true});
  }
  function initProductZoom(scope=document) {
    scope.querySelectorAll('[data-product-zoom-dialog]').forEach(dialog=>{
      if(dialog.dataset.zoomInitialized)return;
      dialog.dataset.zoomInitialized='true';
      dialog.addEventListener('close',()=>{const opener=dialog.zoomOpener;dialog.querySelector('[data-product-zoom-image]')?.removeAttribute('src');dialog.classList.remove('is-zoomed');if(opener?.isConnected)opener.focus({preventScroll:true});});
      dialog.querySelector('[data-product-zoom-image]')?.addEventListener('click',()=>setProductZoom(dialog,Number(dialog.dataset.zoomLevel||1)>1?1:2));
    });
  }
  function initGalleries(context = document) {
    context.querySelectorAll('[data-product-gallery]').forEach(gallery => {
      if (gallery.dataset.initialized) return;
      gallery.dataset.initialized='true';
      const dots = gallery.parentElement.querySelectorAll('[data-gallery-page]');
      let frame;
      gallery.addEventListener('scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const media = [...gallery.children];
          const maxScroll = gallery.scrollWidth - gallery.clientWidth;
          const index = maxScroll > 1 && gallery.scrollLeft >= maxScroll - 1 ? media.length - 1 : media.reduce((nearest,item,i) => Math.abs(galleryMediaOffset(gallery,item)-gallery.scrollLeft) < Math.abs(galleryMediaOffset(gallery,media[nearest])-gallery.scrollLeft) ? i : nearest,0);
          dots.forEach((dot,i) => {dot.classList.toggle('is-active',i===index); if(i===index) dot.setAttribute('aria-current','true'); else dot.removeAttribute('aria-current');});
        });
      },{passive:true});
    });
  }
  async function initRecent(context = document) {
    const product = context.querySelector('[data-product-root]:not([data-quick])');
    if (!product || product.dataset.recentInitialized) return;
    product.dataset.recentInitialized='true';
    const handle=product.dataset.productHandle;
    const previous=storage.read(recentKey).filter(item => item !== handle).slice(0,8);
    storage.write(recentKey,[handle,...previous]);
    const mount=product.querySelector('[data-recent-rail]');
    if (!mount || !previous.length) return;
    const products=await Promise.all(previous.map(item => fetchProduct(item).catch(() => null)));
    mount.innerHTML=products.filter(Boolean).map(storedProductCard).join('');
    product.querySelector('[data-recent-products]').hidden=!products.some(Boolean);
  }
  function initRecommendations(context = document) {
    context.querySelectorAll('[data-product-recommendations]').forEach(section => {
      if (section.dataset.initialized) return;
      section.dataset.initialized='true';
      const load=async () => {
        try {
          const response=await fetch(section.dataset.url,{credentials:'same-origin'});
          if (!response.ok) return;
          const rail=parseHTML(await response.text()).querySelector('[data-recommendations-rail]');
          if (rail?.children.length) {section.querySelector('[data-recommendations-rail]').replaceWith(rail); document.dispatchEvent(new CustomEvent('o2o:content-updated'));}
          else if (!section.querySelector('[data-recommendations-rail]')?.children.length) section.hidden=true;
        } catch {if (!section.querySelector('[data-recommendations-rail]')?.children.length) section.hidden=true;}
      };
      if ('IntersectionObserver' in window) {const observer=new IntersectionObserver(entries => {if(entries.some(entry => entry.isIntersecting)){observer.disconnect();load();}},{rootMargin:'400px'});observer.observe(section);} else load();
    });
  }
  document.addEventListener('submit', event => {
    const form=event.target;
    if (form.matches('[data-product-form]')) {event.preventDefault();addProduct(form).catch(error => {const node=form.querySelector('[data-form-error]');if(node){node.hidden=false;node.textContent=error.message;}});}
  });
  document.addEventListener('change',event => {
    const target=event.target;
    if(target.matches('[data-option-value-id]')) selectVariant(target);
    if(target.matches('[data-cart-quantity]')) changeQuantity(target.closest('[data-cart-key]'),Number(target.value));
    if(target.matches('[data-cart-note]')) {
      clearTimeout(noteTimer);
      const value=target.value;
      noteTimer=setTimeout(() => enqueueCart(async () => {
        await request(jsonRoute(routes.cartUpdate),{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({note:value})});
        const node=document.querySelector('[data-note-status]');if(node)node.textContent=text('noteSaved');
      }).catch(cartError),250);
    }
  });
  document.addEventListener('click',event => {
    const target=event.target.closest('button,a,label.option-button');
    if(!target)return;
    if(target.matches('label.option-button')) {
      const input=target.previousElementSibling;
      if(input?.checked && /custom|made to measure/i.test(input.value)) {
        const root=target.closest('[data-product-root]');
        if(root?.querySelector('[data-product-submit]:not([disabled])'))surfaceOpen(root.dataset.customDrawerId);
      }
    }
    if(target.matches('[data-commerce-open]')) {
      event.preventDefault();
      surfaceOpen(target.dataset.commerceOpen);
    }
    if(target.matches('[data-product-zoom]')) {event.preventDefault();openProductZoom(target);}
    if(target.matches('[data-product-zoom-close]')) {event.preventDefault();target.closest('[data-product-zoom-dialog]')?.close();}
    if(target.matches('[data-product-zoom-in]')) {event.preventDefault();changeProductZoom(target.closest('[data-product-zoom-dialog]'),1);}
    if(target.matches('[data-product-zoom-out]')) {event.preventDefault();changeProductZoom(target.closest('[data-product-zoom-dialog]'),-1);}
    if(target.matches('[data-measure-unit]')) {
      const modal=target.closest('.o2o-size-guide');
      modal.querySelectorAll('[data-size-panel]').forEach(node=>{node.hidden=node.dataset.sizePanel!==target.dataset.measureUnit;});
      modal.querySelectorAll('[data-measure-unit]').forEach(node=>{const active=node===target;node.classList.toggle('is-active',active);node.setAttribute('aria-selected',String(active));});
    }
    if(target.matches('[data-commerce-close]')) {event.preventDefault();surfaceClose(target.dataset.commerceClose);}
    if(target.matches('[data-quick-view],[data-quick-add]')) {event.preventDefault();quickView(target.dataset.quickView || target.dataset.quickAdd,target.hasAttribute('data-quick-add'));}
    if(target.matches('[data-cart-delta]')) {event.preventDefault();const line=target.closest('[data-cart-key]');const input=line?.querySelector('[data-cart-quantity]');if(input)changeQuantity(line,Number(input.value)+Number(target.dataset.cartDelta));}
    if(target.matches('[data-cart-remove]')) {event.preventDefault();changeQuantity(target.closest('[data-cart-key]'),0);}
    if(target.matches('[data-gallery-page]')) {const gallery=target.closest('.pdp-gallery-wrap')?.querySelector('[data-product-gallery]');scrollGalleryToMedia(gallery,gallery?.children[Number(target.dataset.galleryPage)]);}
    if(target.matches('[data-share-product]')) {
      const product=target.closest('[data-product-root]');
      const url=new URL(product.dataset.productUrl,window.location.origin).href;
      const data={title:product.querySelector('.pdp-title')?.textContent,url};
      const share=navigator.share ? navigator.share(data) : navigator.clipboard?.writeText(url).then(() => status(text('linkCopied')));
      share?.catch(error => {if(error.name!=='AbortError')status(text('linkError'));});
    }
  });
  document.addEventListener('o2o:surface-open',event => {
    if(event.detail?.id==='cartDrawer' && Date.now()-lastCartRefresh>1000)refreshCart().catch(cartError);
  });
  document.addEventListener('shopify:section:load',event => {initGalleries(event.target);initProductZoom(event.target);initSizeGuides(event.target);initRecommendations(event.target);initRecent(event.target);syncMobileProductActions();});
  document.addEventListener('o2o:content-updated',scheduleMobileProductActions);
  document.addEventListener('toggle',scheduleMobileProductActions,true);
  document.addEventListener('load',scheduleMobileProductActions,true);
  window.addEventListener('scroll',scheduleMobileProductActions,{passive:true});
  window.addEventListener('resize',scheduleMobileProductActions,{passive:true});
  window.addEventListener('resize',()=>document.querySelectorAll('[data-product-zoom-dialog][open]').forEach(dialog=>setProductZoom(dialog,Number(dialog.dataset.zoomLevel||1))),{passive:true});
  window.visualViewport?.addEventListener('resize',scheduleMobileProductActions,{passive:true});
  window.visualViewport?.addEventListener('scroll',scheduleMobileProductActions,{passive:true});
  document.fonts?.ready.then(scheduleMobileProductActions);
  window.addEventListener('pageshow',event => {if(event.persisted)refreshCart().catch(() => {});});
  initGalleries();initProductZoom();initSizeGuides();initRecent();initRecommendations();syncMobileProductActions();
  window.O2OCommerce={refreshCart,quickView};
})();
